import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { apiGet } from '@/api/client';
import type {
  Company,
  SupportTicket,
  SupportTicketPriority,
  SupportTicketStatus,
} from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Badge, type BadgeTone } from '@/app/components/ui/Badge';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Field, Select } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useAsync } from '@/lib/useAsync';

const STATUSES: readonly SupportTicketStatus[] = ['open', 'in_progress', 'resolved', 'closed'];
const PRIORITIES: readonly SupportTicketPriority[] = ['low', 'normal', 'high', 'urgent'];

function statusTone(status: SupportTicketStatus | undefined): BadgeTone {
  switch (status) {
    case 'open':
      return 'warning';
    case 'in_progress':
      return 'mint';
    case 'closed':
      return 'outline';
    default:
      return 'outline';
  }
}

function priorityTone(priority: SupportTicketPriority | undefined): BadgeTone {
  return priority === 'urgent' || priority === 'high' ? 'danger' : 'outline';
}

/**
 * "Beheer › Support" (PLAN-FASE3.md WP-01): every ticket on the platform,
 * one table, filterable by status and priority — same shape as WP-07's
 * Pilots overview, but the filters go to the server (`GET /support/tickets`
 * accepts `status`/`priority` query params) rather than narrowing a single
 * unfiltered response client-side, since the underlying list is
 * platform-staff-only and has no natural per-company split to fetch once.
 */
export function SupportTicketsPage() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  const tickets = useAsync(
    () =>
      apiGet('/support/tickets', {
        query: {
          ...(statusFilter !== '' ? { status: statusFilter as SupportTicketStatus } : {}),
          ...(priorityFilter !== '' ? { priority: priorityFilter as SupportTicketPriority } : {}),
        },
      }) as Promise<SupportTicket[]>,
    [statusFilter, priorityFilter],
  );
  const companies = useAsync(() => apiGet('/companies') as Promise<Company[]>, []);

  const companyNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const company of companies.data ?? []) {
      if (company.id && company.name) map.set(company.id, company.name);
    }
    return map;
  }, [companies.data]);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('manageSupport.page.title')} lead={t('manageSupport.page.lead')} />

      <form className="flex flex-wrap items-end gap-3">
        <Field label={t('manageSupport.page.filterStatus')} className="w-56">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('manageSupport.page.allStatuses')}</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(`manageSupport.status.${status}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('manageSupport.page.filterPriority')} className="w-56">
          <Select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
            <option value="">{t('manageSupport.page.allPriorities')}</option>
            {PRIORITIES.map((priority) => (
              <option key={priority} value={priority}>
                {t(`manageSupport.priority.${priority}`)}
              </option>
            ))}
          </Select>
        </Field>
      </form>

      <AsyncSection state={tickets} label={t('manageSupport.page.rowsLabel')} skeleton="table">
        {(rows) =>
          rows.length === 0 ? (
            <EmptyState text={t('manageSupport.page.empty')} />
          ) : (
            <DataTable
              rowKey={(ticket) => ticket.id ?? ''}
              rows={rows}
              columns={[
                {
                  key: 'subject',
                  header: t('manageSupport.page.subject'),
                  render: (ticket) => (
                    <Link to={`/manage/support/tickets/${ticket.id}`}>{ticket.subject}</Link>
                  ),
                },
                {
                  key: 'company',
                  header: t('manageSupport.page.company'),
                  render: (ticket) =>
                    (ticket.company_id && companyNames.get(ticket.company_id)) ?? ticket.company_id,
                },
                {
                  key: 'status',
                  header: t('manageSupport.page.status'),
                  render: (ticket) => (
                    <Badge tone={statusTone(ticket.status)}>
                      {ticket.status ? t(`manageSupport.status.${ticket.status}`) : '—'}
                    </Badge>
                  ),
                },
                {
                  key: 'priority',
                  header: t('manageSupport.page.priority'),
                  render: (ticket) => (
                    <Badge tone={priorityTone(ticket.priority)}>
                      {ticket.priority ? t(`manageSupport.priority.${ticket.priority}`) : '—'}
                    </Badge>
                  ),
                },
                {
                  key: 'createdAt',
                  header: t('manageSupport.page.createdAt'),
                  render: (ticket) => ticket.created_at ?? '—',
                },
              ]}
            />
          )
        }
      </AsyncSection>
    </section>
  );
}
